import { motion } from "motion/react";
import { Eye, FileText, BookOpen, CalendarDays } from "lucide-react";
import type { ExamHubResource } from "../../../types/examHub";
import SubjectEmoji from "./SubjectEmoji";

const TYPE_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; color: string; bg: string }
> = {
  notes: {
    label: "Notes",
    icon: <BookOpen className="w-3.5 h-3.5" />,
    color: "text-violet-700",
    bg: "bg-violet-50 border-violet-100",
  },
  tips: {
    label: "Resource",
    icon: <FileText className="w-3.5 h-3.5" />,
    color: "text-blue-700",
    bg: "bg-blue-50 border-blue-100",
  },
  "past-paper": {
    label: "Past Paper",
    icon: <FileText className="w-3.5 h-3.5" />,
    color: "text-orange-700",
    bg: "bg-orange-50 border-orange-100",
  },
  "formula-sheet": {
    label: "Assignment",
    icon: <FileText className="w-3.5 h-3.5" />,
    color: "text-green-700",
    bg: "bg-green-50 border-green-100",
  },
  schedule: {
    label: "Schedule",
    icon: <CalendarDays className="w-3.5 h-3.5" />,
    color: "text-red-700",
    bg: "bg-red-50 border-red-100",
  },
};

export default function ResourceCard({
  note,
  canAccess = true,
}: {
  note: ExamHubResource;
  canAccess?: boolean;
}) {
  const typeConf = TYPE_CONFIG[note.type];
  const hasDownloadUrl = canAccess && Boolean(note.downloadUrl);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col h-full"
    >
      <div
        className="h-1.5 flex-shrink-0"
        style={{ backgroundColor: note.subjectColor }}
      />
      <div className="p-4 flex flex-col flex-1">
        {/* Type badge */}
        <div className="mb-3">
          <span
            className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${typeConf.bg} ${typeConf.color}`}
            style={{ fontWeight: 700 }}
          >
            {typeConf.icon} {typeConf.label}
          </span>
        </div>

        {/* Emoji + subject label */}
        <div className="flex items-center gap-3 mb-2">
          <SubjectEmoji
            subjectEmoji={note.subjectEmoji}
            subjectColor={note.subjectColor}
          />
          <p
            className="text-[11px] uppercase tracking-widest"
            style={{ fontWeight: 700, color: note.subjectColor }}
          >
            {note.subject}
          </p>
        </div>

        {/* Title */}
        <p
          className="text-[13px] text-slate-900 leading-snug mb-3"
          style={{ fontWeight: 700 }}
        >
          {note.title}
        </p>

        {/* Description */}
        <p
          className="text-[12px] text-slate-500 leading-relaxed mb-4"
          style={{ fontWeight: 400 }}
        >
          {note.description}
        </p>

        {/* Meta */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1">
            <FileText className="w-3.5 h-3.5 text-slate-400" />
            <span
              className="text-[11px] text-slate-500"
              style={{ fontWeight: 500 }}
            >
              {note.size}
            </span>
          </div>
          <span
            className="text-[10px] text-slate-400"
            style={{ fontWeight: 400 }}
          >
            {note.uploadedAt}
          </span>
        </div>

        {/* Spacer pushes button to bottom */}
        <div className="flex-1" />

        {!canAccess ? (
          <div
            className="w-full py-2.5 rounded-xl flex items-center justify-center gap-2 text-[12px] text-slate-500 bg-slate-100"
            style={{ fontWeight: 700 }}
          >
            <Eye className="w-3.5 h-3.5" /> Not Registered
          </div>
        ) : hasDownloadUrl ? (
          <a
            href={note.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-2.5 rounded-xl flex items-center justify-center gap-2 text-[12px] text-white transition-all hover:opacity-90"
            style={{ backgroundColor: note.subjectColor, fontWeight: 700 }}
          >
            <Eye className="w-3.5 h-3.5" /> View
          </a>
        ) : (
          <div
            className="w-full py-2.5 rounded-xl flex items-center justify-center gap-2 text-[12px] text-slate-500 bg-slate-100"
            style={{ fontWeight: 700 }}
          >
            <Eye className="w-3.5 h-3.5" /> Coming Soon
          </div>
        )}
      </div>
    </motion.div>
  );
}
